import { supabase } from './supabase';

/*
  The numbers a rep typed that have not reached the database yet.

  **Built 10 September 2026 for job 25, part two**, from the recommendation the
  owner took on 5 September: *"go with your offline recommendation."*

  **What it is for, in one sentence.** A rep counts in a hospital supply room or
  a basement, with a phone in one hand and a shelf in the other. Telling them to
  stop and wait for a signal is telling them to stop working, and telling them
  to type it again later is worse, because by the time the message arrives they
  have moved along the shelf.

  **Why it is written to the phone's own storage and not just held in the page.**
  Group H's README says it plainly and it is the whole reason this file is not
  ten lines: a version that only keeps the numbers in memory loses them when the
  browser closes, WHILE LOOKING LIKE IT DID NOT. That is the same fault this is
  meant to cure, wearing a different coat. So every number goes to
  `localStorage` the moment it is typed, and comes off only when the database
  has confirmed it.

  **And if the phone will not store it, the person is told.** A browser can
  refuse storage: a private window, a full disk, settings that block site data.
  Refusing quietly and carrying on in memory would be the exact fault above, so
  `durable` goes false and the bar says so. Better a rep who knows to stay in
  signal than a rep who loses an afternoon.

  -- Three things that are easy to get wrong and are done deliberately here ----

  **1. The time on a number is when it was TYPED, not when it was sent.** A
  count taken at nine in a basement and sent at eleven in the car park is a
  nine o'clock count. It is carried on the job.

  **2. Order is kept, so once anything is waiting, everything waits.** If a rep
  types 5 on a line and it is held, then types 7 on the same line and that one
  goes straight through, the database ends up saying 5. So the moment this queue
  is not empty, every later number joins the back of it rather than overtaking.

  **3. A refusal is not a dropped signal, and the difference matters.** A write
  the database REFUSES will be refused again in an hour and in a week, so
  queueing it means a bar that never clears and a number that never lands. Only
  what looks like a connection problem is held. Everything else is reported at
  once, the way `save()` in `save.js` already does.
*/

const KEY = 'onhand.outbox.v1';

let jobs = [];
let durable = true;
let flushing = false;
let listeners = [];

/* -- Storage ---------------------------------------------------------------- */

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    // Unreadable is not the same as empty, but there is nothing to be done
    // about it here beyond not crashing. `durable` is set by write(), which is
    // the half that matters: a rep needs to know before they type, not after.
    return [];
  }
}

function write() {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(jobs));
    durable = true;
  } catch {
    durable = false;
  }
}

/* -- Who is watching -------------------------------------------------------- */

function tell() {
  const s = state();
  listeners.forEach(fn => { try { fn(s); } catch { /* one bad listener does not stop the rest */ } });
}

export function subscribe(fn) {
  listeners.push(fn);
  fn(state());
  return () => { listeners = listeners.filter(l => l !== fn); };
}

export function state() {
  return { waiting: jobs.length, durable, offline: isOffline() };
}

export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/*
  **Does this look like a dropped signal, or like a refusal?**

  Getting this wrong in either direction is costly. Treat a refusal as a dropped
  signal and the bar never clears while the number never lands. Treat a dropped
  signal as a refusal and the rep is told their work failed when it is merely
  waiting, and the number is thrown away.

  A browser reports a connection failure as a thrown TypeError whose message is
  some variant of "Failed to fetch" or "NetworkError", and Supabase hands that
  message through on the error object. A refusal comes back with a code from the
  database instead. So: no code plus a fetch-shaped message means the signal.
*/
export function looksLikeConnection(err) {
  if (!err) return false;
  if (isOffline()) return true;
  if (err.code) return false;            // the database answered, and said no
  const m = String(err.message || err).toLowerCase();
  return m.includes('failed to fetch')
      || m.includes('networkerror')
      || m.includes('network request failed')
      || m.includes('load failed');
}

/* -- Putting something in --------------------------------------------------- */

let seq = 0;

/*
  Add a number to the back of the queue. The caller has already put it on the
  screen, which is the point: the rep keeps counting.

  **`sentLine` is how a half-done write is handed over.** Count entry writes the
  line and its history side by side, and the connection can drop between the two
  answers. If the line landed and the history did not, sending the whole job
  again would write the history twice, which is a second row in an audit trail
  saying the same change happened twice. So the caller says which half is done
  and this queue believes it.
*/
export function hold(job) {
  seq += 1;
  const { sentLine, ...rest } = job;
  jobs.push({ ...rest, id: `${Date.now()}-${seq}`, sent: { line: !!sentLine, history: false } });
  write();
  tell();
}

/* Anything waiting means everything waits. See note 2 at the top. */
export function anythingWaiting() {
  return jobs.length > 0;
}

/* -- Getting it out again --------------------------------------------------- */

/*
  Send one job. Each half is marked as it lands, because a half-sent job retried
  from the start would write a second row into the history: the line update is
  the same statement twice and harmless, the history insert is not.
*/
async function send(job) {
  if (!job.sent.line) {
    // Checked for a row actually changed, and who changed it recorded where the
    // shared band reads it: see updateQty in CountEntry.jsx (18 September 2026).
    const { data, error } = await supabase.from('count_line_items').update({
      quantity: job.qty,
      updated_by: job.userId,
      last_updated_by: job.userId,
      last_updated_at: job.at,
      last_updated_name: job.userName,
    }).eq('id', job.itemId).select('id');
    if (error) return error;
    if (!Array.isArray(data) || data.length === 0) return { message: 'That line could not be changed.' };
    job.sent.line = true;
    write();
  }
  if (!job.sent.history) {
    const { error } = await supabase.from('count_line_item_history').insert({
      line_item_id:       job.itemId,
      inventory_count_id: job.countId,
      item_number:        job.itemNumber,
      description:        job.description,
      old_quantity:       job.oldQty,
      new_quantity:       job.qty,
      changed_by:         job.userId || null,
      changed_by_name:    job.userName,
      changed_at:         job.at,
      change_type:        job.changeType,
    });
    // A refused history row is not a refused number: the line is already saved.
    // Only a lost signal holds the job.
    if (error && looksLikeConnection(error)) return error;
    job.sent.history = true;
    write();
  }
  return null;
}

/* Somewhere for a screen to hear about a number that was refused rather than
   delayed, so it can say so instead of leaving the rep to find out at submit. */
let refused = [];
export function onRefused(fn) {
  refused.push(fn);
  return () => { refused = refused.filter(f => f !== fn); };
}

/*
  Try to empty the queue, oldest first, stopping at the first one that will not
  go. **Stopping rather than skipping is the point**: skipping would let a later
  number for the same line land before an earlier one.

  It answers how many went, so the bar can say "All 8 numbers were sent" with a
  number that is true rather than one it assumed.
*/
export async function flush() {
  if (flushing || jobs.length === 0) return 0;
  flushing = true;
  let sent = 0;
  try {
    while (jobs.length > 0) {
      const err = await send(jobs[0]).catch(e => e);
      if (err) {
        /* A refusal will be refused again for ever, and a queue that can never
           empty is worse than a lost number, because it hides every number
           behind it. It comes off the queue and is reported. */
        if (!looksLikeConnection(err)) {
          const bad = jobs.shift();
          write();
          refused.forEach(fn => { try { fn(bad, err); } catch { /* keep going */ } });
          continue;
        }
        break;                       // still no signal. Everything keeps waiting.
      }
      jobs.shift();
      write();
      sent += 1;
      tell();
    }
  } finally {
    flushing = false;
    tell();
  }
  return sent;
}

/* -- Starting up ------------------------------------------------------------ */

/*
  **Read what is already waiting before anything else happens.** A rep who ran
  out of battery in a basement opens OnHand the next morning with numbers still
  on the phone, and they have to be sent rather than sat on. This is why the
  queue lives here rather than inside the count entry screen: the numbers
  outlive the screen that made them.
*/
export function start() {
  jobs = read();
  tell();
  if (typeof window === 'undefined') return () => {};
  const wake = () => { tell(); flush(); };
  window.addEventListener('online', wake);
  window.addEventListener('offline', tell);
  /*
    **A browser saying it is online is not a signal**, and this is why there is
    a timer as well. `navigator.onLine` only knows whether the device thinks it
    is attached to a network: a phone on a hospital wifi with no route out is
    "online" and every write fails. So the queue is also retried on its own,
    and the try itself is the only honest test of whether anything can get out.
  */
  const timer = setInterval(() => { if (jobs.length > 0) flush(); }, 15000);
  flush();
  return () => {
    window.removeEventListener('online', wake);
    window.removeEventListener('offline', tell);
    clearInterval(timer);
  };
}

/* For tests, which need a queue that does not carry yesterday's numbers. */
export function reset() {
  jobs = [];
  durable = true;
  flushing = false;
  try { window.localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
  tell();
}
